/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
/** @module ProductPickup
 * @summary Shows the BOPIS CTA in PDP
 *
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import PickupPromotionBanner from '@tcp/core/src/components/common/molecules/PickupPromotionBanner';
import DynamicFreeSippingModal from '@tcp/core/src/components/common/organisms/ProductPickup/views/DynamicFreeSipping.modal.view';
import getPdpBrandLabel from '@tcp/core/src/components/features/browse/ProductDetail/molecules/ProductBasicInfo/helpers/ProductBasicInfoHelper';
import {
  BOPIS_ITEM_AVAILABILITY,
  BOPIS_ITEM_AVAILABILITY_STATUS,
} from '@tcp/core/src/components/common/organisms/PickupStoreModal/PickUpStoreModal.constants';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import {
  getIconPath,
  isCanada,
  updateStoreDataLocalStorage,
  getBrand,
  setSessionStorage,
  removeSessionStorage,
} from '@tcp/core/src/utils';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import { isClient, changeOnlyFirstLetterToCapital, getDateObject } from '@tcp/core/src/utils/utils';
import {
  COLOR_FITS_SIZES_MAP_PROP_TYPE,
  PRICING_PROP_TYPES,
} from '../../PickupStoreModal/PickUpStoreModal.proptypes';
import { capitalizeEveryFirstLetterOfWord, handleGenericKeyDown } from '../util';
import withStyles from '../../../hoc/withStyles';
import ProductPickupStyles from '../styles/ProductPickup.style';
import DeliveryMethods from '../../../molecules/DeliveryMethods/views';
import { KEY_CODES } from '../../../../../constants/keyboard.constants';
import {
  getProductPickupLabels,
  getQuickViewLabels,
  labelsObject,
} from '../../PickupStoreModal/molecules/PickupStoreSelectionForm/views/PickupStoreSelectionForm.labels';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../utils/labels/labelUtilty';
import { getQuickViewLabelsCopy } from '../../QuickViewModal/container/QuickViewModal.selectors';
import { PLPLabels } from '../../../../../utils/labels/browse/PLP.labels';
import { ProductPickupLabels } from '../../../../../utils/labels/browse/ProductPickup.labels';
import {
  GLOBAL_ACCESSIBILITY_LABELS,
  GlobalAccessibilityLabels,
} from '../../../../../utils/labels/globalAccessibility.labels';

const StoreLocatorModal = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "stor-locator-modal" */
      '@tcp/core/src/components/common/organisms/StoreLocatorModal'
    ),
  { ssr: false }
);

const {
  lbl_Product_pickup_CHANGE_STORE: changeStore,
  lbl_Product_pickup_TITLE_DEFAULT_NOSTORE: defaultNoStore,
  lbl_free_pickup_location: freePickupLocation,
  lbl_Product_pickup_BOPIS_AVAILABLE: bopisAvailable,
  lbl_Product_pickup_BOSS_ONLY_AVAILABLE: bossOnlyAvailable,
  lbl_Product_pickup_UNAVAILABLE_IN_STORES: unavailableInStores,
  lbl_Product_pickup_FIND_STORE: findStore,
  lbl_Product_pickup_PICKUP_IN_STORE: pickupInStore,
  lbl_store_unavailable_error_boss: unavailableErrorBoss,
  lbl_check_other_locations: checkOtherLocations,
  lbl_Product_pickup_BOSS_AVAILABLE: bossAvailable,
  lbl_store_unavailable_error: storeUnavailableError,
  lbl_stores_nearby: storesNearby,
  lbl_Product_pickup_NO_MIN_PURCHASE: noMinPurchase,
  lbl_Product_pickup_FREE_SHIPPING: freeShipping,
  lbl_available_today: lblAvailabletoday,
  lbl_available_in_store_only: lblAvailableInStoreOnly,
} = getProductPickupLabels();

const alsoAvailableIn = getLabelFromCopyOrFromTranslation(
  'PLP.plpTiles.lbl_plp_also_available_in',
  PLPLabels
);

const oosText = 'out of stock';

/**
 *  Describes a general product, not yet specialized by chosing a color, size, etc.
 *  For example, a product shown in reccomendations, or PDP.
 */
export const PRODUCT_INFO_PROP_TYPES = {
  /* this identifies a product for Bazaarvoice (reviews and ratings component) */
  ratingsProductId: PropTypes.string,

  /** This identifies the product regardless of color/fit/size (i.e., changing size/fit/color does not change this value) */
  generalProductId: PropTypes.string.isRequired,
  /** The name of the product to be displayed to the user */
  name: PropTypes.string.isRequired,
  /** Images for this product in different colors.
   * This is an object of key-vale pairs. the key is the color name, and the value has thew shape described below.
   */
  imagesByColor: PropTypes.oneOfType([
    PropTypes.objectOf(
      PropTypes.shape({
        extraImages: PropTypes.arrayOf(
          PropTypes.shape({
            iconSizeImageUrl: PropTypes.string.isRequired,
            regularSizeImageUrl: PropTypes.string.isRequired,
            bigSizeImageUrl: PropTypes.string.isRequired,
            superSizeImageUrl: PropTypes.string.isRequired,
          })
        ),
      })
    ),
    PropTypes.objectOf(
      PropTypes.shape({
        basicImageUrl: PropTypes.string.isRequired,
      })
    ),
  ]),
  /** optional displayNames of the color fit and size (e.g., for gift cards it is {color: 'Design, size: 'Value']) */
  colorFitSizeDisplayNames: PropTypes.shape({
    color: PropTypes.string,
    fit: PropTypes.string,
    size: PropTypes.string,
  }),
  /**
   * The available color fit and size options for this product
   * Organized in a three level nesting (similar to a site navigation) with L1 being the color,
   * L2 being the fit, and L3 being the size
   */
  colorFitsSizesMap: COLOR_FITS_SIZES_MAP_PROP_TYPE,

  /** SEO Friendly URL required to have the image and title linkable */
  pdpUrl: PropTypes.string.isRequired,

  /* Short description of the product. */
  shortDescription: PropTypes.string,

  /* Long description of the product that may include HTML. */
  longDescription: PropTypes.string,

  /** Flags if this SKU/product is a Gift-Card */
  isGiftCard: PropTypes.bool,

  /** Product price, which may be overriden by sku-level price */
  ...PRICING_PROP_TYPES,
};

export const PRODUCT_INFO_PROP_TYPE_SHAPE = PropTypes.shape(PRODUCT_INFO_PROP_TYPES);

const getBopisItemInventoryRes = (
  bopisItemInventory,
  defaultSuggestedStore,
  isBopisMultipleStoreEnabled
) => {
  if (isBopisMultipleStoreEnabled) {
    const defaultStoreId = defaultSuggestedStore && defaultSuggestedStore[0]?.basicInfo?.id;
    const filterInventoryResponse = bopisItemInventory?.inventoryResponse?.filter(
      (store) => store?.storeId === defaultStoreId
    );
    return filterInventoryResponse && filterInventoryResponse[0];
  }
  return (
    bopisItemInventory &&
    bopisItemInventory.inventoryResponse &&
    bopisItemInventory.inventoryResponse[0]
  );
};

const getShouldShowFulfillmentSection = (fromBagPage, isNewPDPEnabled, isNewQVEnabled) => {
  return fromBagPage || (!isNewPDPEnabled && !isNewQVEnabled);
};

class ProductPickup extends React.PureComponent {
  static propTypes = {
    miscInfo: PropTypes.shape({}),
    className: PropTypes.string,

    /**
     * Information regarding the product at the swatch/color level.
     */
    productInfo: PRODUCT_INFO_PROP_TYPE_SHAPE.isRequired,
    /** User's preferred store information */
    userDefaultStore: PropTypes.shape({
      basicInfo: PropTypes.shape({
        /** store id identifier */
        id: PropTypes.string.isRequired,
        /** store Name */
        storeName: PropTypes.string.isRequired,
      }).isRequired,
    }),
    /**
     * User's latitude and longitude coordinates if access is allowed.
     */
    userGeoCoordinates: PropTypes.shape({
      lat: PropTypes.number,
      long: PropTypes.number,
    }),

    /**
     * Item values selected for the product, to use in the BOPIS modal if it
     * gets opened.
     */
    itemValues: PropTypes.shape({
      color: PropTypes.string.isRequired,
      Fit: PropTypes.string,
      Size: PropTypes.string,
      Quantity: PropTypes.number,
    }),

    /**
     * method responsible for triggering the operator method for BopisQuickViewModal
     */
    onPickUpOpenClick: PropTypes.func,
    /**
     * carries the inventory information of the bopis item selected
     */
    bopisItemInventory: PropTypes.arrayOf(PropTypes.object),
    isBopisEligible: PropTypes.bool,
    isBossEligible: PropTypes.bool,
    isSkuResolved: PropTypes.bool,
    showChangeStore: PropTypes.bool,
    pickupTitleText: PropTypes.string,
    isBossEligBossInvAvail: PropTypes.bool,
    isStoreBopisEligible: PropTypes.bool,
    showPickupInfo: PropTypes.bool,
    itemBrand: PropTypes.string,
    isSubmitting: PropTypes.bool,
    isAnchor: PropTypes.bool,
    sizeUnavailable: PropTypes.string,
    onPickupClickAddon: PropTypes.func,
    isOutfitVariant: PropTypes.bool,
    isStoreAndProductBossEligible: PropTypes.bool,
    keepAlive: PropTypes.bool,
    outOfStockLabels: PropTypes.shape({
      itemSoldOutMessage: PropTypes.string,
    }),
    isProductPickup: PropTypes.bool,
    availableTCPmapNewStyleId: PropTypes.shape([]),
    initialMultipackMapping: PropTypes.shape([]),
    selectedAccessories: PropTypes.shape([]),
    setInitialTCPStyleQty: PropTypes.string,
    isNewQVEnabled: PropTypes.bool,
    currentColorEntry: PropTypes.shape({}),
    productInfoFromBag: PropTypes.shape({}).isRequired,
    pickupSelectionUpdate: PropTypes.func,
    isPickUpSelected: PropTypes.string,
    isNewPDPEnabled: PropTypes.bool,
    fromBagPage: PropTypes.bool,
    checkForOOSForVariant: PropTypes.bool.isRequired,
    isAllSizeDisabled: PropTypes.bool,
    isBossEnabled: PropTypes.bool,
    isBopisEnabled: PropTypes.bool,
    isNewPJPPDPRedesignEnabled: PropTypes.bool,
    isBopisPromoDiscountBannerEnabled: PropTypes.bool,
  };

  static defaultProps = {
    miscInfo: {},
    userDefaultStore: {
      basicInfo: {
        id: '',
        storeName: '',
      },
    },
    userGeoCoordinates: null,
    itemValues: {
      color: '',
      Fit: '',
      Size: '',
      Quantity: null,
    },
    onPickUpOpenClick: null,
    bopisItemInventory: [],
    className: '',
    isBopisEligible: false,
    isBossEligible: false,
    isSkuResolved: false,
    showChangeStore: false,
    pickupTitleText: '',
    isBossEligBossInvAvail: false,
    isStoreBopisEligible: false,
    itemBrand: '',
    showPickupInfo: false,
    isSubmitting: false,
    isAnchor: false,
    sizeUnavailable: 'Size unavailable online?',
    onPickupClickAddon: () => {},
    isOutfitVariant: false,
    isStoreAndProductBossEligible: false,
    keepAlive: false,
    outOfStockLabels: {
      itemSoldOutMessage: '',
    },
    isProductPickup: true,
    availableTCPmapNewStyleId: [],
    initialMultipackMapping: [],
    selectedAccessories: [],
    setInitialTCPStyleQty: '',
    isNewQVEnabled: false,
    currentColorEntry: {},
    pickupSelectionUpdate: () => {},
    isPickUpSelected: '',
    isNewPDPEnabled: false,
    fromBagPage: false,
    isAllSizeDisabled: false,
    isBossEnabled: false,
    isBopisEnabled: false,
    isNewPJPPDPRedesignEnabled: false,
    isBopisPromoDiscountBannerEnabled: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      storeData: {},
      modalStoreSearchData: null,
      bopisStoreSelectorModalOpen: false,
      isDFSModalOpen: false,
    };
    this.handlePickupModalClick = this.handlePickupModalClick.bind(this);
    this.bossChangeStoreCall = this.bossChangeStoreCall.bind(this);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.handleBopisStoreModalClose = this.handleBopisStoreModalClose.bind(this);

    this.labelsObj = getQuickViewLabels();
    this.quickViewLabels = getQuickViewLabelsCopy(this.labelsObj);
    const {
      needItASAP = '',
      pickItUp = '',
      getItToday = '',
      deliveryMethod = '',
      shipIt = '',
      freeShipping: freeShippingQV = '',
      noMinimumRequired = '',
    } = this.quickViewLabels || {};
    this.needItASAP = needItASAP;
    this.pickItUp = pickItUp;
    this.getItToday = getItToday;
    this.deliveryMethod = deliveryMethod;
    this.shipIt = shipIt;
    this.freeShippingQV = freeShippingQV;
    this.noMinimumRequired = noMinimumRequired;
  }

  componentDidMount() {
    const { defaultStoreData } = this.props;
    const { storeData } = this.state;
    if (defaultStoreData && !isEqual(storeData, defaultStoreData)) {
      this.setState({ storeData: defaultStoreData });
    }
  }

  componentDidUpdate(prevProps) {
    const { defaultStoreData } = this.props;
    if (prevProps.defaultStoreData !== defaultStoreData) {
      this.setState({ storeData: defaultStoreData });
    }
  }

  getPageName = (productInfo) => {
    let pageName = '';
    const productId = productInfo && productInfo.generalProductId.split('_')[0];
    const productName = productInfo && productInfo.name.toLowerCase();
    if (productId) {
      pageName = `product:${productId}:${productName}`;
    }
    return {
      pageName,
    };
  };

  bagPackCheck = () => {
    const { selectedAccessories, productInfo } = this.props;
    const { accessoriesData = {} } = productInfo || {};
    const { customizationType = '' } = accessoriesData || {};

    if (
      customizationType === 'mandatory' &&
      selectedAccessories &&
      selectedAccessories?.length === 0
    ) {
      return true;
    }
    return false;
  };

  /**
   * @method handlePickupModalClick -
   * method is responsible for invoking the method for open pickup modal
   */

  handlePickupModalClick = (e, fromAvailableStores) => {
    const {
      onPickUpOpenClick,
      productInfo,
      onPickupClickAddon,
      isBopisEligible,
      isBossEligible,
      itemBrand,
      isProductPickup,
      availableTCPmapNewStyleId,
      initialMultipackMapping,
      setInitialTCPStyleQty,
      alternateBrand,
      isPickUpSelected,
      selectedSizeValue,
      isPDP,
      fromAvailableStoresClick,
      trackClickAnalyticsBopisFilter,
      nearestAvailableStore,
      isOutfitVariant,
      userDefaultStore,
      multipackProduct,
      partIdInfo,
      getAllNewMultiPack,
      multiPackCount,
    } = this.props;
    const checkNearestStoresHaveDefaultStore = this.checkNearestStoresHaveDefaultStore();
    const noOfStores = nearestAvailableStore?.length - checkNearestStoresHaveDefaultStore;
    const { storeBossInfo } = userDefaultStore;
    const isStoreBossEligible = storeBossInfo?.isBossEligible;
    if (fromAvailableStoresClick && isPickUpSelected === 'bopis') {
      const fromClick = true;
      this.trackBopisMultiStoreAnalytics(
        nearestAvailableStore,
        trackClickAnalyticsBopisFilter,
        noOfStores,
        fromClick
      );
    }
    if (fromAvailableStoresClick && isPickUpSelected === 'boss') {
      this.trackBossMultiStoreAnalytics(
        trackClickAnalyticsBopisFilter,
        isStoreBossEligible,
        getLabelFromCopyOrFromTranslation('lbl_store_unavailable_error_boss', ProductPickupLabels),
        true
      );
    }
    if (isPickUpSelected === 'boss') {
      setSessionStorage({ key: 'pickupValue', value: isPickUpSelected });
    } else {
      removeSessionStorage('pickupValue');
    }
    e.preventDefault();
    if (!selectedSizeValue || this.bagPackCheck()) {
      const { storeData } = this.state;
      const { onStoreChangeLinkClick, resetSuggestedStores } = this.props;
      if (onStoreChangeLinkClick) {
        onStoreChangeLinkClick();
      }
      this.setState({
        bopisStoreSelectorModalOpen: true,
        modalStoreSearchData: storeData,
      });
      resetSuggestedStores([]);
    } else {
      fromAvailableStoresClick(fromAvailableStores);
      onPickUpOpenClick({
        generalProductId: productInfo.generalProductId,
        isBopisCtaEnabled: isBopisEligible,
        isBossCtaEnabled: isBossEligible,
        currentProduct: { ...productInfo, itemBrand },
        colorProductId: productInfo.generalProductId,
        isProductPickup,
        availableTCPmapNewStyleId,
        availableTCPMultipackProductMapping: initialMultipackMapping,
        initialSelectedQty: setInitialTCPStyleQty,
        setInitialTCPStyleQty,
        alternateBrand,
        isPickUpSelected,
        isBopisPickup: isPickUpSelected === 'bopis',
        isPDP,
        isOutfitVariant,
        ...(multipackProduct
          ? {
              newPartNumber: {
                multiPackData: {
                  multiPackCount,
                  newPartNumber: multipackProduct,
                  getNewMultiPackAllColor: getAllNewMultiPack,
                  partIdDetail: partIdInfo,
                },
              },
            }
          : {}),
      });
      if (onPickupClickAddon) {
        onPickupClickAddon();
      }
    }
  };

  getBopisInventoryStatus = (recommendationsBopisInventory) => {
    const { bopisItemInventory, isBopisMultipleStoreEnabled, defaultSuggestedStore } = this.props;
    let bopisItemInventoryRes;
    if (recommendationsBopisInventory) {
      bopisItemInventoryRes = getBopisItemInventoryRes(
        recommendationsBopisInventory,
        defaultSuggestedStore,
        isBopisMultipleStoreEnabled
      );
    } else {
      bopisItemInventoryRes = getBopisItemInventoryRes(
        bopisItemInventory,
        defaultSuggestedStore,
        isBopisMultipleStoreEnabled
      );
    }
    if (bopisItemInventoryRes?.status === BOPIS_ITEM_AVAILABILITY_STATUS.AVAILABLE) {
      return capitalizeEveryFirstLetterOfWord(BOPIS_ITEM_AVAILABILITY_STATUS.INSTOCK);
    }
    if (bopisItemInventoryRes?.status === BOPIS_ITEM_AVAILABILITY.LIMITED) {
      return BOPIS_ITEM_AVAILABILITY.LIMITED;
    }
    if (bopisItemInventoryRes?.status === BOPIS_ITEM_AVAILABILITY_STATUS.UNAVAILABLE) {
      return BOPIS_ITEM_AVAILABILITY_STATUS.OUTOFSTOCK;
    }
    if (bopisItemInventoryRes?.status === 'Limited Availability' && isBopisMultipleStoreEnabled) {
      return BOPIS_ITEM_AVAILABILITY_STATUS.LIMITEDSTOCK;
    }
    return bopisItemInventoryRes && bopisItemInventoryRes.status;
  };

  oldPickupCheck = (showPickupInfo, isNewQVEnabled) => {
    return showPickupInfo && !isNewQVEnabled;
  };

  itemBrandCheck = () => {
    const { productInfo, alternateBrand } = this.props;
    const primaryBrand = productInfo?.primaryBrand;
    const prodBrand = getBrand() && getBrand().toUpperCase();
    return getPdpBrandLabel({
      primaryBrand,
      alternateBrand,
      prodBrand,
      getBrand: true,
    });
  };

  showFindInStore = () => {
    const { showPickupInfo, isSkuResolved } = this.props;
    let { userDefaultStore } = this.props;
    userDefaultStore = this.userStoreFromStorage(userDefaultStore);
    return showPickupInfo && userDefaultStore && isSkuResolved;
  };

  /**
   * @method handleChangeStoreOnKeyPress
   * handles the change store modal when Enter key is pressed post tabbing on the link
   */
  handleChangeStoreOnKeyPress = (event, fromAvailableStores) =>
    handleGenericKeyDown(
      event,
      KEY_CODES.ENTER,
      this.handlePickupModalClick.bind(event, fromAvailableStores)
    );

  /**
   * @method renderPickupTitle
   * @param {object} miscInfo carries the information of the product with
   * respective color product
   * @param {object} userDefaultStore carries the information of the store user's
   * default store
   */
  renderPickupTitle() {
    const {
      isSkuResolved,
      isBopisEligible,
      showChangeStore,
      pickupTitleText,
      isBossEligBossInvAvail,
    } = this.props;
    let { userDefaultStore } = this.props;
    userDefaultStore = this.userStoreFromStorage(userDefaultStore);
    if (isSkuResolved) {
      if (userDefaultStore) {
        if (showChangeStore) {
          /**
           * if the sku is resolved and the user has added a store to the account
           * then it @returns - store name and the link to change the store
           * */
          return (
            <React.Fragment>
              <BodyCopy
                className="store-name capFirstLetter"
                fontSize="fs16"
                fontFamily="secondary"
                fontWeight="semibold"
                component="span"
              >
                {userDefaultStore?.basicInfo?.storeName}
              </BodyCopy>
              <Anchor
                fontSizeVariation="medium"
                className="change-store-link"
                role="button"
                tabIndex="0"
                onKeyDown={this.handleChangeStoreOnKeyPress}
                handleLinkClick={this.handlePickupModalClick}
                underline
                noLink
              >
                {renderLabel(changeStore, 'lbl_Product_pickup_CHANGE_STORE')}
              </Anchor>
            </React.Fragment>
          );
        }
        return (
          <BodyCopy fontSize="fs16" fontWeight="semibold" fontFamily="secondary">
            {renderLabel(defaultNoStore, 'lbl_Product_pickup_TITLE_DEFAULT_NOSTORE')}
          </BodyCopy>
        );
      }
      if (isBopisEligible && !isBossEligBossInvAvail) {
        // bopis only
        /**
         * @returns if the product is only bopis eligible and the sku is resolved
         * then it @returns {labels?.PRODUCT_BOPIS}
         */
        return (
          <BodyCopy fontSize="fs16" fontWeight="semibold" fontFamily="secondary">
            {pickupTitleText}
          </BodyCopy>
        );
      }
      /**
       * @returns if the product is only boss eligible and the sku is resolved
       * then it @returns {labels?.TITLE_DEFAULT_NOSTORE}
       */
      return (
        <BodyCopy fontSize="fs16" fontWeight="semibold" fontFamily="secondary">
          {pickupTitleText}
        </BodyCopy>
      );
    }
    return (
      <BodyCopy fontSize="fs16" fontWeight="semibold" fontFamily="secondary">
        {pickupTitleText}
      </BodyCopy>
    );
  }

  /**
   * @method renderPickupTitleNewQV
   * @param {object} userDefaultStore carries the information of the store user's
   * default store
   */
  renderPickupTitleNewQV() {
    const { isNewPDPEnabled, isNewQVEnabled } = this.props;
    let { userDefaultStore } = this.props;
    userDefaultStore = this.userStoreFromStorage(userDefaultStore);
    const pickupIconPath = `location-icon`;
    if (userDefaultStore) {
      /**
       * it @returns - store name and the link to change the store
       * */
      return (
        <React.Fragment>
          <img
            className="store-icon"
            alt={renderLabel(
              getLabelFromCopyOrFromTranslation(
                `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_pickup_icon`,
                GlobalAccessibilityLabels
              ),
              'lbl_pickup_icon'
            )}
            src={getIconPath('store-star')}
          />
          <div className="store-info-container">
            {isNewPDPEnabled || isNewQVEnabled ? (
              <img
                className="location-icon-store-name"
                alt="location icon"
                src={getIconPath(pickupIconPath)}
              />
            ) : null}
            <BodyCopy
              className="store-name quick-view-drawer-redesign capFirstLetter"
              fontFamily="secondary"
              fontWeight="extrabold"
              component="span"
            >
              {userDefaultStore?.basicInfo?.storeName}
            </BodyCopy>
          </div>

          {isNewPDPEnabled ? (
            <div className="text-right">
              <Anchor
                fontSizeVariation="medium"
                className="change-store-link quick-view-drawer-redesign"
                role="button"
                tabIndex="0"
                onKeyDown={this.handleChangeStoreOnKeyPress}
                handleLinkClick={this.handlePickupModalClick}
                underline
                noLink
              >
                {renderLabel(changeStore, 'lbl_Product_pickup_CHANGE_STORE')
                  ?.replace('(', '')
                  ?.replace(')', '')}
              </Anchor>
            </div>
          ) : (
            <Anchor
              fontSizeVariation="medium"
              className="change-store-link quick-view-drawer-redesign"
              role="button"
              tabIndex="0"
              onKeyDown={this.handleChangeStoreOnKeyPress}
              handleLinkClick={this.handlePickupModalClick}
              underline
              noLink
            >
              {renderLabel(changeStore, 'lbl_Product_pickup_CHANGE_STORE')
                ?.replace('(', '')
                ?.replace(')', '')}
            </Anchor>
          )}
        </React.Fragment>
      );
    }
    return null;
  }

  renderFreePickUpStore() {
    let { userDefaultStore } = this.props;
    userDefaultStore = this.userStoreFromStorage(userDefaultStore);
    if (userDefaultStore) {
      return (
        <BodyCopy
          className="free-pickup-store"
          fontFamily="secondary"
          fontWeight="semibold"
          component="div"
          fontSize="fs14"
        >
          {userDefaultStore?.basicInfo?.storeName}
        </BodyCopy>
      );
    }
    return '';
  }

  checkNearestStoresHaveDefaultStore = (isRecommendationsQV) => {
    const { nearestAvailableStore, userDefaultStore, recommendationStores } = this.props;
    let arr = [];
    if (isRecommendationsQV) {
      arr = recommendationStores?.filter(
        (store) => userDefaultStore?.basicInfo?.storeName === store?.basicInfo?.storeName
      );
    } else {
      arr = nearestAvailableStore?.filter(
        (store) => userDefaultStore?.basicInfo?.storeName === store?.basicInfo?.storeName
      );
    }

    return arr.length;
  };

  bossChangeStoreCall = (e) => {
    const { bossMultipleStoreCall } = this.props;
    bossMultipleStoreCall();
    this.handlePickupModalClick(e, true);
  };

  bossChangeStoreOnKeyPress = (event) => {
    handleGenericKeyDown(event, KEY_CODES.ENTER, this.bossChangeStoreCall.bind(event));
  };

  /**
   *
   * @param {*} bossMultipleStoreCheck | kill switch value of Boss MultiStore
   * @returns Find in store box UI
   */
  renderPickupWidget = () => {
    const {
      userDefaultStore,
      defaultSuggestedStore,
      trackClickAnalyticsBopisFilter,
      isBossCalled,
      updateIsBossCalled,
      shouldShowSizeError,
      displayErrorMessage,
      earlyAccessDisable,
    } = this.props;
    const distance = defaultSuggestedStore?.[0]?.distance
      ? defaultSuggestedStore?.[0]?.distance
      : userDefaultStore?.distance;

    const { storeBossInfo } = userDefaultStore;
    const isStoreBossEligible = storeBossInfo?.isBossEligible;
    if (!isBossCalled) {
      this.trackBossMultiStoreAnalytics(
        trackClickAnalyticsBopisFilter,
        isStoreBossEligible,
        renderLabel(unavailableErrorBoss, 'lbl_store_unavailable_error_boss')
      );
      updateIsBossCalled();
    }
    if (!shouldShowSizeError) {
      displayErrorMessage(false);
    }
    return isStoreBossEligible ? (
      <div className="free-pickup-container">
        <BodyCopy
          className="free-pick-up"
          fontFamily="secondary"
          fontWeight="bold"
          component="span"
          fontSize="fs16"
        >
          {renderLabel(freePickupLocation, 'lbl_free_pickup_location')}
        </BodyCopy>
        <BodyCopy
          className="fav-store"
          fontFamily="secondary"
          fontWeight="bold"
          component="div"
          fontSize="fs16"
        >
          {this.renderFreePickUpStore()}
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="fav-icon"
          >
            <path
              d="M13.8855 6.22448C14.1098 5.99523 13.986 5.59594 13.676 5.54869L9.49364 4.9114C9.37072 4.89273 9.26432 4.81165 9.20944 4.69469L7.33874 0.720719C7.20014 0.426427 6.79974 0.426427 6.66114 0.720719L4.791 4.69469C4.73556 4.81165 4.62916 4.89273 4.50624 4.9114L0.323847 5.54869C0.0138847 5.59594 -0.109596 5.99523 0.114405 6.22448L3.14095 9.31762C3.22971 9.40862 3.27059 9.53987 3.24959 9.6682L2.53502 14.0359C2.4821 14.3597 2.80635 14.6064 3.08355 14.4536L6.8241 12.3915C6.93442 12.3308 7.06602 12.3308 7.17578 12.3915L10.9166 14.4536C11.1938 14.6064 11.5178 14.3597 11.4649 14.0359L10.7506 9.6682C10.7296 9.53987 10.7702 9.40862 10.8589 9.31762L13.8855 6.22448Z"
              fill="black"
            />
          </svg>
        </BodyCopy>

        {!distance && <div className="fixed-height-link" />}
        {distance && (
          <BodyCopy
            fontFamily="secondary"
            fontWeight="semibold"
            component="span"
            fontSize="fs12"
            className="fav-store-distance"
          >
            {`${distance} mi.`}
          </BodyCopy>
        )}
        <div className="also-available-link">
          {earlyAccessDisable ? (
            <div className="disabled-link">
              {renderLabel(changeStore, 'lbl_Product_pickup_CHANGE_STORE')
                ?.replace('(', '')
                ?.replace(')', '')}
            </div>
          ) : (
            <Anchor
              onKeyDown={(e) => this.bossChangeStoreOnKeyPress(e)}
              handleLinkClick={(e) => this.bossChangeStoreCall(e)}
              underline
              noLink
            >
              {renderLabel(changeStore, 'lbl_Product_pickup_CHANGE_STORE')
                ?.replace('(', '')
                ?.replace(')', '')}
            </Anchor>
          )}
        </div>
      </div>
    ) : (
      <div className="check-all-stores">
        <BodyCopy
          className="no-pickup-store-error"
          fontFamily="secondary"
          fontWeight="semibold"
          component="div"
          fontSize="fs14"
        >
          <div className="non-availability-icon" />
          <div className="pickup-error-container">
            {`${renderLabel(unavailableErrorBoss, 'lbl_store_unavailable_error_boss')} `}
            <Anchor
              className="check-other-stores-url"
              onKeyDown={(e) => this.bossChangeStoreOnKeyPress(e)}
              handleLinkClick={(e) => this.bossChangeStoreCall(e)}
              underline
              noLink
            >
              {renderLabel(checkOtherLocations, 'lbl_check_other_locations')}
            </Anchor>
          </div>
        </BodyCopy>
      </div>
    );
  };

  chekStatusStyle = (status) => {
    if (status?.toLowerCase() === oosText) return 'oos-cls';
    return 'pickup-today';
  };

  checkLimitedStock = (status) => {
    if (status?.toLowerCase() === 'limited stock') {
      return <span className="ltd-stock">{status}</span>;
    }
    return null;
  };

  checkAvailableStock = (status) => {
    if (status?.toLowerCase() === oosText) {
      return status;
    }
    if (status) {
      return changeOnlyFirstLetterToCapital(
        renderLabel(this.labelsObj?.lbl_cartTile_pickUpToday, 'lbl_cartTile_pickUpToday')
      );
    }
    return status;
  };

  renderLimitedStock = (inventoryStatus) => this.checkLimitedStock(inventoryStatus);

  renderCurrentStoreDetails = (inventoryStatus, isAllSizeDisableForBopisStore) => (
    <div
      className={`current-store-details ${isAllSizeDisableForBopisStore ? 'oos-store-cls' : ''}`}
    >
      <span className={this.chekStatusStyle(inventoryStatus)}>
        {!isAllSizeDisableForBopisStore && this.checkAvailableStock(inventoryStatus)}
        {isAllSizeDisableForBopisStore && BOPIS_ITEM_AVAILABILITY_STATUS.OUTOFSTOCK}
      </span>{' '}
      <span className="at">
        {renderLabel(
          this.labelsObj?.lbl_pickup_instead_free_order,
          'lbl_pickup_instead_free_order'
        )}
      </span>
      <Anchor
        onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
        handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
        underline
        noLink
      >
        {this.renderFreePickUpStore()}
      </Anchor>
    </div>
  );

  renderAlsoAvailableLink = (isOutOfStock, noOfStores) => {
    return (
      <>
        {noOfStores !== 0 && !isOutOfStock && (
          <Anchor
            onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
            handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
            underline
            noLink
            className={'also-available-link'}
          >
            {`${changeOnlyFirstLetterToCapital(
              renderLabel(alsoAvailableIn, 'lbl_plp_also_available_in')
            )} ${noOfStores} ${renderLabel(storesNearby, 'lbl_stores_nearby')}`}
          </Anchor>
        )}
        {noOfStores !== 0 && isOutOfStock && (
          <>
            <span className="available-today">
              {renderLabel(lblAvailabletoday, 'lbl_available_today')}
            </span>
            <Anchor
              onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
              handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
              underline
              noLink
              className={'also-available-link'}
            >
              {`${noOfStores} ${renderLabel(storesNearby, 'lbl_stores_nearby')}`}
            </Anchor>
          </>
        )}
      </>
    );
  };

  renderNotAvailableInStore = (storeUnavailableError1, checkOtherLocations1) => (
    <div className="free-pick-new-design">
      <div className="check-all-stores">
        <BodyCopy
          className="no-pickup-store-error"
          fontFamily="secondary"
          fontWeight="semibold"
          component="div"
          fontSize="fs14"
        >
          <div className="non-availability-icon" />
          <div className="pickup-error-container">
            {`${renderLabel(storeUnavailableError1, 'lbl_store_unavailable_error')} `}
            <Anchor
              className="check-other-stores-url"
              onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
              handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
              underline
              noLink
            >
              {renderLabel(checkOtherLocations1, 'lbl_check_other_locations')}
            </Anchor>
          </div>
        </BodyCopy>
      </div>
    </div>
  );

  renderAvailableInStore = (inventoryStatus, isOutOfStock, noOfStores, showInStoreMsg) => (
    <div className="free-pick-new-design">
      <div className="in-store-only">
        {showInStoreMsg && renderLabel(lblAvailableInStoreOnly, 'lbl_available_in_store_only')}
      </div>
      {this.renderLimitedStock(inventoryStatus)}
      {this.renderCurrentStoreDetails(inventoryStatus)}
      <div className="also-available-link">
        {this.renderAlsoAvailableLink(isOutOfStock, noOfStores)}
      </div>
    </div>
  );

  getFinalBopisInventoryStatus = (
    isRecommendationsQV,
    recommendationsBopisInventory,
    bopisItemInventoryStatus
  ) => {
    if (isRecommendationsQV) {
      return this.getBopisInventoryStatus(recommendationsBopisInventory);
    }
    return bopisItemInventoryStatus;
  };

  /**
   *
   * @param {*} bopisItemInventoryStatus  | stock availabilty
   * @returns Find in store box UI
   */

  renderAvailableFreePickUp(bopisItemInventoryStatus, storeSpecialMsg) {
    const {
      nearestAvailableStore,
      nearestAvailableStoreLoading,
      trackClickAnalyticsBopisFilter,
      isCalled,
      updateIsCalled,
      defaultSuggestedStore,
      recommendationStores,
      isRecommendationsQV,
      shouldShowSizeError,
      displayErrorMessage,
      recommendationsBopisInventory,
      isPickupPromotionPDPEnabled,
      isPDP,
      showInStoreMsg,
      storeSpecialList,
      storeSpecials,
      earlyAccessDisable,
    } = this.props;
    const checkNearestStoresHaveDefaultStore =
      this.checkNearestStoresHaveDefaultStore(isRecommendationsQV);
    const noOfStores = isRecommendationsQV
      ? recommendationStores?.length - checkNearestStoresHaveDefaultStore
      : nearestAvailableStore?.length - checkNearestStoresHaveDefaultStore;
    const nearestAvailableStoreList = isRecommendationsQV
      ? recommendationStores
      : nearestAvailableStore;
    const finalBopisInventoryStatus = this.getFinalBopisInventoryStatus(
      isRecommendationsQV,
      recommendationsBopisInventory,
      bopisItemInventoryStatus
    );

    const availabilityClass =
      finalBopisInventoryStatus === 'In Stock' ? 'in-stock' : 'ooo-or-limited';

    const inventoryStockStatus = getLabelFromCopyOrFromTranslation(
      'Browse.ProductPickup.lbl_Limited_Stock',
      labelsObject
    );
    const distance = defaultSuggestedStore?.[0]?.distance;
    if (!nearestAvailableStoreLoading && !isCalled && isClient()) {
      this.trackBopisMultiStoreAnalytics(
        nearestAvailableStore,
        trackClickAnalyticsBopisFilter,
        noOfStores
      );
      updateIsCalled();
    }
    if (!shouldShowSizeError) {
      displayErrorMessage(false);
    }
    if (nearestAvailableStoreLoading && !(isPickupPromotionPDPEnabled && isPDP)) {
      return (
        <div className="free-pickup-container">
          <BodyCopy
            className="free-pick-up"
            fontFamily="secondary"
            fontWeight="bold"
            component="span"
            fontSize="fs16"
          >
            {renderLabel(freePickupLocation, 'lbl_free_pickup_location')}
          </BodyCopy>
          <BodyCopy
            className="fav-store"
            fontFamily="secondary"
            fontWeight="bold"
            component="div"
            fontSize="fs16"
          >
            {this.renderFreePickUpStore()}
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="fav-icon"
            >
              <path
                d="M13.8855 6.22448C14.1098 5.99523 13.986 5.59594 13.676 5.54869L9.49364 4.9114C9.37072 4.89273 9.26432 4.81165 9.20944 4.69469L7.33874 0.720719C7.20014 0.426427 6.79974 0.426427 6.66114 0.720719L4.791 4.69469C4.73556 4.81165 4.62916 4.89273 4.50624 4.9114L0.323847 5.54869C0.0138847 5.59594 -0.109596 5.99523 0.114405 6.22448L3.14095 9.31762C3.22971 9.40862 3.27059 9.53987 3.24959 9.6682L2.53502 14.0359C2.4821 14.3597 2.80635 14.6064 3.08355 14.4536L6.8241 12.3915C6.93442 12.3308 7.06602 12.3308 7.17578 12.3915L10.9166 14.4536C11.1938 14.6064 11.5178 14.3597 11.4649 14.0359L10.7506 9.6682C10.7296 9.53987 10.7702 9.40862 10.8589 9.31762L13.8855 6.22448Z"
                fill="black"
              />
            </svg>
          </BodyCopy>
          <div className="availability-status-container">
            {finalBopisInventoryStatus && (
              <BodyCopy
                className={`availability-status ${availabilityClass}`}
                fontFamily="secondary"
                fontWeight="bold"
                component="span"
                fontSize="fs10"
              >
                {finalBopisInventoryStatus}
              </BodyCopy>
            )}
          </div>

          <div className="free-pickup-fixed" />
        </div>
      );
    }
    if (isPickupPromotionPDPEnabled && isPDP) {
      const isAvailableInStore = nearestAvailableStore?.length >= 1;
      return isAvailableInStore
        ? this.renderAvailableInStore(
            bopisItemInventoryStatus,
            bopisItemInventoryStatus?.toLowerCase() === oosText || bopisItemInventoryStatus === '',
            noOfStores,
            showInStoreMsg
          )
        : this.renderNotAvailableInStore(storeUnavailableError, checkOtherLocations);
    }

    return this.renderBopisWidget(
      nearestAvailableStoreList,
      finalBopisInventoryStatus,
      availabilityClass,
      distance,
      noOfStores,
      storeSpecialMsg,
      storeSpecialList,
      storeSpecials,
      inventoryStockStatus,
      earlyAccessDisable
    );
  }

  renderBopisWidget(
    nearestAvailableStore,
    bopisItemInventoryStatus,
    availabilityClass,
    distance,
    noOfStores,
    storeSpecialMsg,
    storeSpecialList,
    storeSpecials,
    inventoryStockStatus,
    earlyAccessDisable
  ) {
    const inventoryOosStatus = getLabelFromCopyOrFromTranslation(
      'Browse.ProductPickup.lbl_Product_pickup_OUT_OF_STOCK_TEXT_redesign',
      labelsObject
    );
    const inventoryInsStatus = getLabelFromCopyOrFromTranslation(
      'Browse.ProductPickup.lbl_In_Stock',
      labelsObject
    );
    const normalizedStatus = bopisItemInventoryStatus?.toLowerCase();
    const resolvedStatus =
      {
        'limited stock': inventoryStockStatus,
        'out of stock': inventoryOosStatus,
        'in stock': inventoryInsStatus,
      }[normalizedStatus] || bopisItemInventoryStatus;

    return nearestAvailableStore?.length >= 1 ? (
      <div className="free-pickup-container">
        <BodyCopy
          className="free-pick-up"
          fontFamily="secondary"
          fontWeight="bold"
          component="span"
          fontSize="fs16"
        >
          {renderLabel(freePickupLocation, 'lbl_free_pickup_location')}
        </BodyCopy>
        <BodyCopy
          className="fav-store"
          fontFamily="secondary"
          fontWeight="bold"
          component="div"
          fontSize="fs16"
        >
          {this.renderFreePickUpStore()}
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="fav-icon"
          >
            <path
              d="M13.8855 6.22448C14.1098 5.99523 13.986 5.59594 13.676 5.54869L9.49364 4.9114C9.37072 4.89273 9.26432 4.81165 9.20944 4.69469L7.33874 0.720719C7.20014 0.426427 6.79974 0.426427 6.66114 0.720719L4.791 4.69469C4.73556 4.81165 4.62916 4.89273 4.50624 4.9114L0.323847 5.54869C0.0138847 5.59594 -0.109596 5.99523 0.114405 6.22448L3.14095 9.31762C3.22971 9.40862 3.27059 9.53987 3.24959 9.6682L2.53502 14.0359C2.4821 14.3597 2.80635 14.6064 3.08355 14.4536L6.8241 12.3915C6.93442 12.3308 7.06602 12.3308 7.17578 12.3915L10.9166 14.4536C11.1938 14.6064 11.5178 14.3597 11.4649 14.0359L10.7506 9.6682C10.7296 9.53987 10.7702 9.40862 10.8589 9.31762L13.8855 6.22448Z"
              fill="black"
            />
          </svg>
        </BodyCopy>
        <div className="availability-status-container">
          {bopisItemInventoryStatus && (
            <BodyCopy
              className={`availability-status ${availabilityClass}`}
              fontFamily="secondary"
              fontWeight="bold"
              component="span"
              fontSize="fs10"
            >
              {resolvedStatus}
            </BodyCopy>
          )}
        </div>
        {!distance && <div className="fixed-height-link" />}
        {distance && (
          <BodyCopy
            fontFamily="secondary"
            fontWeight="semibold"
            component="span"
            fontSize="fs12"
            className="fav-store-distance"
          >
            {`${distance} mi.`}
          </BodyCopy>
        )}
        {!isEmpty(storeSpecialList) &&
          storeSpecials &&
          nearestAvailableStore?.length >= 1 &&
          noOfStores !== 0 && (
            <BodyCopy
              fontFamily="secondary"
              fontWeight="bold"
              component="span"
              fontSize="fs14"
              className="fav-store-distance"
            >
              {storeSpecialMsg}
            </BodyCopy>
          )}

        <div className="also-available-link">
          {earlyAccessDisable && (
            <div className="disabled-link">
              {`${changeOnlyFirstLetterToCapital(
                renderLabel(alsoAvailableIn, 'lbl_plp_also_available_in')
              )} ${noOfStores} ${renderLabel(storesNearby, 'lbl_stores_nearby')}`}
            </div>
          )}
          {nearestAvailableStore?.length >= 1 && noOfStores !== 0 && !earlyAccessDisable && (
            <Anchor
              onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
              handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
              underline
              noLink
            >
              {`${changeOnlyFirstLetterToCapital(
                renderLabel(alsoAvailableIn, 'lbl_plp_also_available_in')
              )} ${noOfStores} ${renderLabel(storesNearby, 'lbl_stores_nearby')}`}
            </Anchor>
          )}
        </div>
      </div>
    ) : (
      nearestAvailableStore?.length < 1 && (
        <div className="check-all-stores">
          <BodyCopy
            className="no-pickup-store-error"
            fontFamily="secondary"
            fontWeight="semibold"
            component="div"
            fontSize="fs14"
          >
            <div className="non-availability-icon" />
            <div className="pickup-error-container">
              {`${renderLabel(storeUnavailableError, 'lbl_store_unavailable_error')} `}
              {earlyAccessDisable ? (
                <div className="disabled-link">
                  {renderLabel(checkOtherLocations, 'lbl_check_other_locations')}
                </div>
              ) : (
                <Anchor
                  className="check-other-stores-url"
                  onKeyDown={(e) => this.handleChangeStoreOnKeyPress(e, true)}
                  handleLinkClick={(e) => this.handlePickupModalClick(e, true)}
                  underline
                  noLink
                >
                  {renderLabel(checkOtherLocations, 'lbl_check_other_locations')}
                </Anchor>
              )}
            </div>
          </BodyCopy>
        </div>
      )
    );
  }

  /**
   * @member renderPickupInfo
   * @description this method returns the information of pickup on the
   * respective store based on different scenarios
   * @param {object} miscInfo carries the product information
   * with respective color id
   * @param {object} userDefaultStore carries the information of User's default store
   * @return if the sku is resolved and the product is available in store
   * or both BOSS and BOPIS
   * then is @returns - item availability information along with the
   * pickup today label and the BOSS dates information with the boss pickup
   * label
   * @return if the sku is resolved and the product is only available for BOPIS
   * then it @returns - item availability information with the BOPIS only pickup
   * label
   * @return if the sku is resolved and the product is avialable only for BOSS
   * then it @returns - the boss dates information along with the BOSS only
   * pickup labels
   */

  renderPickupInfo() {
    const { isStoreBopisEligible, isStoreAndProductBossEligible } = this.props;

    const bopisItemInventoryStatus = this.getBopisInventoryStatus();

    const bopisStatusAvailability =
      isStoreBopisEligible && isStoreAndProductBossEligible ? (
        <BodyCopy fontSize="fs12" fontFamily="secondary">
          <BodyCopy
            fontSize="fs12"
            className="availability"
            fontWeight="extrabold"
            fontFamily="secondary"
            color="success"
            component="span"
          >
            {`${bopisItemInventoryStatus}!`}
          </BodyCopy>
          {renderLabel(bopisAvailable, 'lbl_Product_pickup_BOPIS_AVAILABLE')}
        </BodyCopy>
      ) : null;

    if (isStoreBopisEligible && isStoreAndProductBossEligible) {
      return (
        <div className="pickup-info">
          {bopisStatusAvailability}
          <br />
          <span className="boss-message">
            {renderLabel(bossAvailable, 'lbl_Product_pickup_BOSS_AVAILABLE')}
            <PickupPromotionBanner bossBanner />
          </span>
        </div>
      );
    }
    if (isStoreBopisEligible) {
      return <div className="pickup-info">{bopisStatusAvailability}</div>;
    }

    if (isStoreAndProductBossEligible) {
      return (
        <div className="pickup-info">
          <span className="boss-message">
            {renderLabel(bossOnlyAvailable, 'lbl_Product_pickup_BOSS_ONLY_AVAILABLE')}
            <PickupPromotionBanner bossBanner />
          </span>
        </div>
      );
    }
    return null;
  }

  renderPickupInfoNewQV(pickupIconPath) {
    const { isNewQVEnabled } = this.props;
    if (isNewQVEnabled) {
      return (
        <div className="pickup-info">
          <div className="pickup-icon-header">
            <img
              className="pickup-icon quick-view-drawer-redesign"
              alt={renderLabel(
                getLabelFromCopyOrFromTranslation(
                  `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_pickup_icon`,
                  GlobalAccessibilityLabels
                ),
                'lbl_pickup_icon'
              )}
              src={getIconPath(pickupIconPath)}
            />
            <BodyCopy
              fontSize="fs14"
              fontWeight="extrabold"
              fontFamily="secondary"
              component="span"
              className="header-pickup-line1"
            >
              {renderLabel(this.pickItUp, 'lbl_pick_it_up')}
            </BodyCopy>
          </div>
          <BodyCopy
            fontSize="fs12"
            fontFamily="secondary"
            className="sub-header-pickup line2 quick-view-drawer-redesign"
          >
            {renderLabel(this.needItASAP, 'lbl_need_it_asap')}
          </BodyCopy>
          <BodyCopy
            fontSize="fs12"
            fontFamily="secondary"
            className="sub-header-pickup line3 quick-view-drawer-redesign"
          >
            {renderLabel(this.getItToday, 'lbl_get_it_today')}
          </BodyCopy>
        </div>
      );
    }
    return null;
  }

  renderPickupInfoError() {
    return (
      <BodyCopy
        fontSize="fs10"
        fontWeight="regular"
        fontFamily="secondary"
        color="error"
        className="error-pickup-info"
      >
        {renderLabel(unavailableInStores, 'lbl_Product_pickup_UNAVAILABLE_IN_STORES')}
        UNAVAILABLE IN STORES.
      </BodyCopy>
    );
  }

  renderSoldOutError = () => {
    const { keepAlive, outOfStockLabels } = this.props;
    return keepAlive ? (
      <BodyCopy color="red.500" fontFamily="secondary" fontSize="fs10">
        {outOfStockLabels.itemSoldOutMessage}
      </BodyCopy>
    ) : null;
  };

  renderFulFillmentSection = (
    isPickUpSelected,
    shippingIconPath,
    showPickupInfo,
    pickupIconPath,
    bopisStatusAvailability,
    pageName
  ) => {
    const { isNewQVEnabled, pickupSelectionUpdate, isSubmitting, isNewPDPEnabled, fromBagPage } =
      this.props;
    return isNewQVEnabled && !fromBagPage ? (
      <div className="bottom-section quick-view-drawer-redesign">
        <BodyCopy
          className="fulfillment-section-heading quick-view-drawer-redesign"
          fontSize="fs14"
          fontFamily="secondary"
          fontWeight="extrabold"
        >
          {renderLabel(this.deliveryMethod, 'lbl_delivery_method')}
        </BodyCopy>
        <div className="pickup-sub-container quick-view-drawer-redesign">
          <div className="pickup-header quick-view-drawer-redesign">
            <div
              className={`title-pickup-section-shipping quick-view-drawer-redesign ${
                isPickUpSelected === 'home' ? 'selected' : ''
              }`}
              onClick={() => pickupSelectionUpdate('home')}
              onKeyDown={() => pickupSelectionUpdate('home')}
              role="button"
              tabIndex="0"
            >
              <div className="shipping-text-section quick-view-drawer-redesign">
                <div className="header-image-wrapper">
                  <img
                    className="shipping-icon quick-view-drawer-redesign"
                    alt={renderLabel(
                      getLabelFromCopyOrFromTranslation(
                        `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_shipping_icon`,
                        GlobalAccessibilityLabels
                      ),
                      'lbl_shipping_icon'
                    )}
                    src={getIconPath(shippingIconPath)}
                  />
                  <BodyCopy
                    fontSize="fs14"
                    fontWeight="extrabold"
                    fontFamily="secondary"
                    component="span"
                    className="header-shipit-line1"
                  >
                    {renderLabel(this.shipIt, 'lbl_ship_it')}
                  </BodyCopy>
                </div>
                <BodyCopy
                  fontSize="fs12"
                  fontFamily="secondary"
                  className="sub-header-shipit line2 quick-view-drawer-redesign"
                >
                  {renderLabel(this.freeShippingQV, 'lbl_free_shipping')}
                </BodyCopy>
                <BodyCopy
                  fontSize="fs12"
                  fontFamily="secondary"
                  className="sub-header-shipit line3 quick-view-drawer-redesign"
                >
                  {renderLabel(this.noMinimumRequired, 'lbl_no_min_req')}
                </BodyCopy>
              </div>
            </div>
          </div>
          <div className="pickup-content quick-view-drawer-redesign">
            <div
              className={`pickup-section quick-view-drawer-redesign ${
                isPickUpSelected === 'pickup' ? 'selected' : ''
              }`}
              onClick={() => pickupSelectionUpdate('pickup')}
              onKeyDown={() => pickupSelectionUpdate('pickup')}
              role="button"
              tabIndex="0"
            >
              <div className="pickup-details quick-view-drawer-redesign">
                {this.oldPickupCheck(showPickupInfo, isNewQVEnabled) && this.renderPickupInfo()}
                {isNewQVEnabled && this.renderPickupInfoNewQV(pickupIconPath)}
              </div>
            </div>
          </div>
        </div>
        {isPickUpSelected === 'pickup' ? (
          <div className="store-section-quick-view-drawer-redesign-container">
            <div className="store-section-quick-view-drawer-redesign">
              {this.renderPickupTitleNewQV()}
              {bopisStatusAvailability}
            </div>
          </div>
        ) : null}
      </div>
    ) : (
      !isNewPDPEnabled && !fromBagPage && (
        <div className="pickup-sub-container">
          <div className="pickup-header">
            <div className="title-pickup-section">
              <img
                className="shipping-icon"
                alt={renderLabel(
                  getLabelFromCopyOrFromTranslation(
                    `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_shipping_icon`,
                    GlobalAccessibilityLabels
                  ),
                  'lbl_shipping_icon'
                )}
                src={getIconPath('fast-shipping')}
              />
              <div className="shipping-text-section">
                <BodyCopy
                  fontSize="fs16"
                  fontWeight="semibold"
                  fontFamily="secondary"
                  component="span"
                >
                  {renderLabel(freeShipping, 'lbl_Product_pickup_FREE_SHIPPING')}
                </BodyCopy>
                <BodyCopy fontSize="fs12" fontFamily="secondary" className="sub-header-pickup">
                  {renderLabel(noMinPurchase, 'lbl_Product_pickup_NO_MIN_PURCHASE')}
                </BodyCopy>
              </div>
            </div>
          </div>
          <div className="pickup-content">
            <div className="pickup-section">
              <div className="title-pickup-section">
                <img
                  className="pickup-icon"
                  alt={renderLabel(
                    getLabelFromCopyOrFromTranslation(
                      `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_pickup_icon`,
                      GlobalAccessibilityLabels
                    ),
                    'lbl_pickup_icon'
                  )}
                  src={getIconPath('marker-icon')}
                />
              </div>
              <div className="pickup-details">
                {this.renderPickupTitle()}
                {showPickupInfo && this.renderPickupInfo()}
              </div>
            </div>
            <ClickTracker
              clickData={{
                customEvents: ['event131'],
                pageName,
              }}
              className="btn-find-in-store-wrapper"
            >
              <Button
                className="button-find-in-store"
                buttonVariation="fixed-width"
                fill="BLACK"
                disabled={isSubmitting}
                onClick={this.handlePickupModalClick}
              >
                {this.showFindInStore()
                  ? renderLabel(pickupInStore, 'lbl_Product_pickup_PICKUP_IN_STORE')
                  : renderLabel(findStore, 'lbl_Product_pickup_FIND_STORE')}
              </Button>
            </ClickTracker>
          </div>
        </div>
      )
    );
  };

  userStoreFromStorage = (userDefaultStore) => {
    let userStore = userDefaultStore;
    if (userStore) {
      const currentDate = getDateObject();
      if (!userStore?.favStoreTimeStamp && !userStore?.akamaiStoreTimeStamp) {
        userStore.favStoreTimeStamp = currentDate;
      }
      updateStoreDataLocalStorage(userStore);
    } else {
      userStore = JSON.parse(getLocalStorage('defaultStore')) || '';
    }
    return userStore;
  };

  checkRenderPickupSection = (storeCityName, storeStateName, isPickUpSelected) => {
    return (
      storeCityName &&
      storeStateName &&
      (isPickUpSelected === 'bopis' || isPickUpSelected === 'boss')
    );
  };

  getOOSClassName = (bopisItemInventoryStatus) => {
    return bopisItemInventoryStatus === 'Out Of Stock' || bopisItemInventoryStatus === 'UNAVAILABLE'
      ? ' out-of-stock'
      : '';
  };

  renderBopisStatus = (bopisItemInventoryStatus, isBopis, isBoss, shouldShowFulfillment) => {
    const {
      isBopisEligible,
      isStoreBopisEligible,
      isStoreAndProductBossEligible,
      isAllSizeDisabled,
    } = this.props;
    if (!shouldShowFulfillment) {
      return (
        (bopisItemInventoryStatus && isBopis && isBopisEligible) ||
        (isBoss && isStoreAndProductBossEligible && !isAllSizeDisabled)
      );
    }
    return isStoreBopisEligible && isStoreAndProductBossEligible;
  };

  getIsBopisProduct = (isBopisEnabled, isBopisEligible) => {
    return isBopisEnabled && isBopisEligible;
  };

  handleBopisStoreModalClose() {
    this.setState({
      modalStoreSearchData: null,
      bopisStoreSelectorModalOpen: false,
    });
  }

  onStoreChange = (store) => {
    const { isBopisFilterOn, onSubmit, formValues = {}, getProducts, updateFormValue } = this.props;
    const storeFilterId = [`v_Quantity_${store?.basicInfo?.id?.slice(-4)}:[1 TO *]`];
    let updatedFormValues = {};
    if (isBopisFilterOn) {
      updateFormValue('bopisStoreId', storeFilterId);
      updatedFormValues = { ...formValues, bopisStoreId: storeFilterId };
      onSubmit(updatedFormValues, false, getProducts);
    }
  };

  hideBopisCheck = () => {
    const {
      isBopisEnabled,
      isBopisEligible,
      miscInfo,
      isStoreSpecialsPriceOn,
      storeSpecials,
      shouldAllowOnlineProductPickup,
    } = this.props;
    const isBopisProduct = this.getIsBopisProduct(isBopisEnabled, isBopisEligible);

    const isOnlineExclusive =
      miscInfo?.badge1?.defaultBadge && miscInfo?.badge1?.defaultBadge === 'ONLINE EXCLUSIVE';
    const isOnlineExclusiveNew = isOnlineExclusive && !(isStoreSpecialsPriceOn && storeSpecials);

    if (shouldAllowOnlineProductPickup) {
      return !isBopisProduct;
    }

    return isOnlineExclusiveNew || !isBopisProduct;
  };

  hideBoss = () => {
    const { isBossEnabled } = this.props;
    return isCanada() || !isBossEnabled;
  };

  renderOldOrNewPickup = (
    bopisMultipleStoreCheck,
    userDefaultStore,
    storeCityName,
    storeStateName,
    isSkuResolved,
    bopisStatusAvailability,
    bopisItemInventoryStatus,
    shouldShowSizeError,
    bossMultipleStoreCheck,
    storeSpecialMsg
  ) => {
    const { isAllSizeDisabled, isAllSizeDisableForBopisStore } = this.props;
    return (
      <>
        {bopisMultipleStoreCheck && !bossMultipleStoreCheck && (
          <div className="store-section-quick-view-drawer-redesign">
            {this.renderPickupTitleNewQV()}
            {userDefaultStore?.basicInfo && storeCityName && storeStateName && (
              <BodyCopy
                className="city-state quick-view-drawer-redesign"
                fontFamily="secondary"
                fontWeight="semibold"
                component="span"
                fontSize="fs12"
              >
                {`${storeCityName}, ${storeStateName}`}
              </BodyCopy>
            )}
            {isSkuResolved ? bopisStatusAvailability : null}
          </div>
        )}
        {!bopisMultipleStoreCheck &&
          !shouldShowSizeError &&
          !this.hideBopisCheck() &&
          this.renderAvailableFreePickUp(bopisItemInventoryStatus, storeSpecialMsg)}
        {isAllSizeDisableForBopisStore &&
          this.renderCurrentStoreDetails(bopisItemInventoryStatus, isAllSizeDisableForBopisStore)}
        {bossMultipleStoreCheck &&
          !shouldShowSizeError &&
          !this.hideBoss() &&
          !isAllSizeDisabled &&
          this.renderPickupWidget()}
      </>
    );
  };

  detailClick = (e) => {
    e.preventDefault();
    setTimeout(() => {
      const { isDFSModalOpen } = this.state;
      this.setState({ isDFSModalOpen: !isDFSModalOpen });
    }, 0);
  };

  renderFreeShippingMessage = (
    isDynamicFreeShippingEnabled,
    freeShippingThreshold,
    isPickUpSelected,
    freeShipsMsg,
    isFreeShippingOnFulfillmentTileEnabled
  ) => {
    return isDynamicFreeShippingEnabled &&
      isPickUpSelected === 'home' &&
      !isFreeShippingOnFulfillmentTileEnabled ? (
      <>
        <div className="free-ship-message">
          {' '}
          {freeShipsMsg && freeShipsMsg[0] + (freeShippingThreshold || 0) + freeShipsMsg[1]}
          <Anchor
            noLink
            handleLinkClick={this.detailClick}
            title={'Details'}
            className="free-ship-anchor"
          >
            {'Details'}
          </Anchor>
        </div>
      </>
    ) : null;
  };

  trackBopisMultiStoreAnalytics = (
    nearestAvailableStore,
    trackClickAnalyticsBopisFilter,
    noOfStores,
    fromClick
  ) => {
    const userDefaultFavStore = JSON.parse(getLocalStorage('defaultStore')) || {};
    let analyticValue = `pdp_fav_${noOfStores}_store${fromClick ? '_click' : ''}`;
    if (userDefaultFavStore?.akamaiStoreFlag) {
      analyticValue = `pdp_non-fav_${noOfStores}_store${fromClick ? '_click' : ''}`;
    }
    if (noOfStores === 0) {
      analyticValue = `pdp_no-store_check other location${fromClick ? '_click' : ''}`;
    }
    trackClickAnalyticsBopisFilter({
      bopis_multistore_cd133: analyticValue,
      customEvents: ['event147'],
    });
  };

  trackBossMultiStoreAnalytics = (
    trackClickAnalyticsBopisFilter,
    isStoreBossEligible,
    errorMsg,
    fromClick
  ) => {
    const userDefaultFavStore = JSON.parse(getLocalStorage('defaultStore')) || {};
    let analyticValue = `pdp_boss_fav${fromClick ? '_change_store' : ''}`;
    if (userDefaultFavStore?.akamaiStoreFlag) {
      analyticValue = `pdp_boss_non-fav${fromClick ? '_change_store' : ''}`;
    }
    if (!isStoreBossEligible) {
      analyticValue = `pdp_boss_no-store_${errorMsg}${fromClick ? '_change_store' : ''}`;
    }
    trackClickAnalyticsBopisFilter({
      bopis_multistore_cd133: analyticValue,
      customEvents: ['event147'],
    });
  };

  checkIfStoreSpecialPricePresent = (isStoreSpecialsPriceOn, storeSpecials) => {
    return isStoreSpecialsPriceOn && storeSpecials;
  };

  checkIfOnlineExclusive = (isOnlineExclusive, storeSpecailPresent) => {
    return isOnlineExclusive && !storeSpecailPresent;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const {
      className,
      showPickupInfo,
      isSubmitting,
      isAnchor,
      sizeUnavailable,
      isOutfitVariant,
      productInfo,
      isPickUpSelected,
      pickupSelectionUpdate,
      isNewQVEnabled,
      checkForOOSForVariant,
      isNewPDPEnabled,
      miscInfo,
      isAllSizeDisabled,
      isBossEnabled,
      isBopisEnabled,
      isSkuResolved,
      isBopisEligible,
      trackClickAnalyticsBopisFilter,
      shippingMethodSelected,
      isNewPJPPDPRedesignEnabled,
      trackAnalyticsClick,
      eddData,
      isVariantsHasPreOrder,
      isShowShipToHomeEddEnabled,
      isBopisMultipleStoreEnabled,
      shouldShowSizeError,
      displayErrorMessage,
      isBossMultipleStoreEnabled,
      isPDP,
      isQuickView,
      isDynamicFreeShippingEnabled,
      freeShippingThreshold,
      dynamicShippingCharge,
      isDisplayPickupTimeEnabled,
      storeHoursThreshold,
      isFullDateDisplayEnabled,
      isBopisPromoDiscountBannerEnabled,
      isStoreSpecialsPriceOn,
      setMethod,
      storeSpecials,
      shouldAllowOnlineProductPickup,
      isFreeShippingOnFulfillmentTileEnabled,
    } = this.props;
    let { userDefaultStore } = this.props;
    const { modalStoreSearchData, bopisStoreSelectorModalOpen, isDFSModalOpen } = this.state;
    userDefaultStore = this.userStoreFromStorage(userDefaultStore);
    const { pageName } = this.getPageName(productInfo);
    const { isStoreBopisEligible, fromBagPage } = this.props;
    const isBopisProduct = this.getIsBopisProduct(isBopisEnabled, isBopisEligible);
    const pickupIconPath = `location-icon`;
    const shippingIconPath = `standard-shipping`;
    const storeCityName = userDefaultStore?.basicInfo?.address?.city || '';
    const storeStateName = userDefaultStore?.basicInfo?.address?.state || '';
    const isBopis = isPickUpSelected === 'bopis';
    const isBoss = isPickUpSelected === 'boss';
    const bopisItemInventoryStatus = isBoss ? 'Ship' : this.getBopisInventoryStatus();
    const labelStore = isBoss ? 'to this store' : 'at this store';
    const bopisMultipleStoreCheck =
      !isBopisMultipleStoreEnabled || bopisItemInventoryStatus === 'Ship';
    const bossMultipleStoreCheck = isBossMultipleStoreEnabled && isBoss;
    const isStoreSpecialsPricePresent = this.checkIfStoreSpecialPricePresent(
      isStoreSpecialsPriceOn,
      storeSpecials
    );
    const shouldShowFulfillment = getShouldShowFulfillmentSection(
      fromBagPage,
      isNewPDPEnabled,
      isNewQVEnabled
    );

    const freeShipsMsg = renderLabel(
      getDynamicLabelValues('dynamic.browse_d.lbl_ships_free_msg'),
      'lbl_ships_free_msg'
    )?.split('##');

    const storeSpecialMsg = renderLabel(
      getDynamicLabelValues('dynamic.browse_d.lbl_banner_store_special_msg'),
      'lbl_banner_store_special_msg'
    );

    const bopisStatusAvailability = this.renderBopisStatus(
      bopisItemInventoryStatus,
      isBopis,
      isBoss,
      shouldShowFulfillment
    ) ? (
      <BodyCopy fontFamily="secondary" className="availability-wrapper quick-view-drawer-redesign">
        <BodyCopy
          className={`availability quick-view-drawer-redesign${this.getOOSClassName(
            bopisItemInventoryStatus
          )}`}
          fontWeight="extrabold"
          fontFamily="secondary"
          component="span"
        >
          {`${bopisItemInventoryStatus}`}
        </BodyCopy>
        <BodyCopy
          className="label-availability quick-view-drawer-redesign"
          fontWeight="normal"
          fontFamily="secondary"
          component="span"
        >
          {labelStore}
        </BodyCopy>
      </BodyCopy>
    ) : null;

    return (
      <React.Fragment>
        {isOutfitVariant && (
          <ClickTracker
            clickData={{
              customEvents: ['event131'],
              pageName,
            }}
          >
            <Button
              className="button-find-in-store"
              buttonVariation="fixed-width"
              fill="BLACK"
              disabled={isSubmitting}
              onClick={this.handlePickupModalClick}
            >
              {showPickupInfo
                ? renderLabel(pickupInStore, 'lbl_Product_pickup_PICKUP_IN_STORE')
                : renderLabel(findStore, 'lbl_Product_pickup_FIND_STORE')}
            </Button>
          </ClickTracker>
        )}
        {!isAnchor && !isOutfitVariant ? (
          <div className={className}>
            {shouldShowFulfillment ? (
              this.renderFulFillmentSection(
                isPickUpSelected,
                shippingIconPath,
                showPickupInfo,
                pickupIconPath,
                bopisStatusAvailability,
                pageName
              )
            ) : (
              <>
                <DeliveryMethods
                  className={className}
                  isPickUpSelected={isPickUpSelected}
                  isNewQVEnabled={isNewQVEnabled}
                  quickViewLabels={this.quickViewLabels}
                  pickupSelectionUpdate={pickupSelectionUpdate}
                  checkForOOSForVariant={checkForOOSForVariant}
                  shippingMethodSelected={shippingMethodSelected}
                  isVariantsHasPreOrder={isVariantsHasPreOrder}
                  isShowShipToHomeEddEnabled={isShowShipToHomeEddEnabled}
                  eddData={eddData}
                  isOnlineExclusive={this.checkIfOnlineExclusive(
                    miscInfo?.badge1?.defaultBadge === 'ONLINE EXCLUSIVE',
                    isStoreSpecialsPricePresent
                  )}
                  isBossEnabled={isBossEnabled}
                  isBopisEnabled={isBopisProduct}
                  miscInfo={miscInfo}
                  isAllSizeDisabled={isAllSizeDisabled}
                  userDefaultStore={userDefaultStore}
                  isStoreBopisEligible={isStoreBopisEligible}
                  isCanada={isCanada()}
                  brand={this.itemBrandCheck()}
                  fulfillmentGroup
                  isNewPJPPDPRedesignEnabled={isNewPJPPDPRedesignEnabled}
                  isBopisMultipleStoreEnabled={isBopisMultipleStoreEnabled}
                  isBossMultipleStoreEnabled={isBossMultipleStoreEnabled}
                  shouldShowSizeError={shouldShowSizeError}
                  displayErrorMessage={displayErrorMessage}
                  isPDP={isPDP}
                  isQuickView={isQuickView}
                  isDynamicFreeShippingEnabled={isDynamicFreeShippingEnabled}
                  isDisplayPickupTimeEnabled={isDisplayPickupTimeEnabled}
                  storeHoursThreshold={storeHoursThreshold}
                  isFullDateDisplayEnabled={isFullDateDisplayEnabled}
                  isBopisPromoDiscountBannerEnabled={isBopisPromoDiscountBannerEnabled}
                  isStoreSpecialsPricePresent={isStoreSpecialsPricePresent}
                  shouldAllowOnlineProductPickup={shouldAllowOnlineProductPickup}
                  setMethod={setMethod}
                  freeShippingThreshold={freeShippingThreshold}
                  freeShipsMsg={freeShipsMsg}
                  isFreeShippingOnFulfillmentTileEnabled={isFreeShippingOnFulfillmentTileEnabled}
                />
                {freeShippingThreshold > 0 &&
                  this.renderFreeShippingMessage(
                    isDynamicFreeShippingEnabled,
                    freeShippingThreshold,
                    isPickUpSelected,
                    freeShipsMsg,
                    isFreeShippingOnFulfillmentTileEnabled
                  )}
                {this.checkRenderPickupSection(storeCityName, storeStateName, isPickUpSelected) ? (
                  <div className="store-section-quick-view-drawer-redesign-container">
                    {this.renderOldOrNewPickup(
                      bopisMultipleStoreCheck,
                      userDefaultStore,
                      storeCityName,
                      storeStateName,
                      isSkuResolved,
                      bopisStatusAvailability,
                      bopisItemInventoryStatus,
                      shouldShowSizeError,
                      bossMultipleStoreCheck,
                      storeSpecialMsg
                    )}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : (
          !isOutfitVariant && (
            <p className="size-unavailable">
              <span className="unavailable-text">{sizeUnavailable}</span>
              <span className="size-find-in-store">
                <Anchor
                  noLink
                  handleLinkClick={this.handlePickupModalClick}
                  title={renderLabel(findStore, 'lbl_Product_pickup_FIND_STORE')}
                  className="size-unavailable-anchor"
                >
                  {renderLabel(findStore, 'lbl_Product_pickup_FIND_STORE')}
                </Anchor>
              </span>
            </p>
          )
        )}
        {bopisStoreSelectorModalOpen && (
          <StoreLocatorModal
            isOpen={bopisStoreSelectorModalOpen}
            onClose={this.handleBopisStoreModalClose}
            onStoreChange={() => this.onStoreChange}
            modalStoreSearchData={modalStoreSearchData}
            trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
            trackAnalyticsClick={trackAnalyticsClick}
          />
        )}
        {/* {isBopisEligible && (
            <ContentSlot contentSlotName="pdp_bopis_promo" className="product-details-bopis-promo" />
          )} */}
        <DynamicFreeSippingModal
          openState={isDFSModalOpen}
          onRequestClose={() => {
            this.setState({
              isDFSModalOpen: false,
            });
          }}
          freeShippingThreshold={freeShippingThreshold}
          dynamicShippingCharge={dynamicShippingCharge}
          showShipToStoreMsg={!this.hideBoss()}
        />
      </React.Fragment>
    );
  }
}

export default withStyles(ProductPickup, ProductPickupStyles);
